/**
 * InlineAnchors — text-match anchored hover-link renderer (v2 chain format).
 *
 * The Lead's brief (Apr 27 2026): "links in the scripts so when they hover
 * over something it brings up a box of information from Opus 4.6."
 *
 * Each step's `body` is an array of narration chunks. Each chunk has a `text`
 * field plus an `anchors[]` list of {text, source_id, context}. We split the
 * narration on each anchor's matched substring and wrap that substring in a
 * focusable <span> that pops a tooltip showing:
 *   • Authors (year)
 *   • Title
 *   • Journal volume(pages)
 *   • Role badge (SUPPORTING / CONTRADICTING / COMPLICATING / PRIMARY_POSITION)
 *   • Opus 4.6 context (the auditor's contextual blurb for this anchor)
 *   • View source → link (opens DOI/url in new tab)
 *
 * Methodology principle: every visible claim is one hover from its receipt,
 * with the auditor's analysis attached. No bottom-stacked list — clean text.
 */
import { useState, useRef } from 'react';
import { ExternalLink } from 'lucide-react';

const ROLE_COLOR = {
  SUPPORTING: '#4A90D9',
  CONTRADICTING: '#D94A4A',
  COMPLICATING: '#D9A04A',
  PRIMARY_POSITION: '#9C7BFF',
};

function AnchorTooltip({ source, anchor, anchorRect, sourceId }) {
  if (!source || !anchorRect) return null;
  const role = source.role || 'SUPPORTING';
  const roleColor = ROLE_COLOR[role] || '#D4A84A';
  const cite = [
    source.authors && `${source.authors} (${source.year || '?'})`,
    source.title,
    source.journal && [source.journal, source.volume, source.pages].filter(Boolean).join(' '),
  ].filter(Boolean);

  const style = {
    top: anchorRect.bottom + window.scrollY + 6,
    left: Math.max(12, Math.min(anchorRect.left + window.scrollX - 12, window.innerWidth - 360)),
    position: 'absolute',
    width: 'min(92vw, 360px)',
    zIndex: 60,
  };

  return (
    <div
      role="tooltip"
      data-testid={`anchor-tooltip-${sourceId}`}
      style={style}
      className="bg-[#0A0A0A] border shadow-[0_8px_30px_rgba(0,0,0,0.6)] p-3 space-y-2 pointer-events-auto"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          data-testid={`anchor-role-${sourceId}`}
          className="font-hud text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded-sm"
          style={{ color: roleColor, border: `1px solid ${roleColor}55`, background: `${roleColor}15` }}
        >
          {role.replace('_', ' ')}
        </span>
        {source.year && (
          <span className="font-mono text-[10px] text-white/55">{source.year}</span>
        )}
      </div>

      <div className="space-y-0.5">
        {cite[0] && (
          <div className="font-mono text-[11px] text-white/85 leading-snug">{cite[0]}</div>
        )}
        {cite[1] && (
          <div className="font-historical text-sm md:text-base text-[#D4A84A] leading-snug">
            {cite[1]}
          </div>
        )}
        {cite[2] && (
          <div className="font-mono text-[10px] text-white/55">{cite[2]}</div>
        )}
      </div>

      {anchor?.context && (
        <div className="border-l-2 pl-2 mt-2 py-1" style={{ borderColor: roleColor }}>
          <div className="font-hud text-[9px] tracking-widest uppercase mb-1" style={{ color: roleColor }}>
            From the auditor
          </div>
          <p className="font-mono text-[11px] text-white/80 leading-relaxed">
            {anchor.context}
          </p>
        </div>
      )}

      {source.url && (
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`anchor-link-${sourceId}`}
          className="inline-flex items-center gap-1 font-hud text-[10px] tracking-widest text-[#2F8A92] hover:text-[#D4A84A]"
        >
          View source <ExternalLink size={10} />
        </a>
      )}
    </div>
  );
}

function AnchorSpan({ children, anchor, source, sourceId }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const role = source?.role || 'SUPPORTING';
  const roleColor = ROLE_COLOR[role] || '#D4A84A';

  const onShow = () => {
    if (!ref.current) return;
    setRect(ref.current.getBoundingClientRect());
    setOpen(true);
  };
  const onHide = () => setOpen(false);

  if (!source) {
    return <span className="text-white/85">{children}</span>;
  }

  return (
    <>
      <span
        ref={ref}
        data-testid={`anchor-${sourceId}`}
        data-source-id={sourceId}
        data-role={role}
        onMouseEnter={onShow}
        onMouseLeave={onHide}
        onFocus={onShow}
        onBlur={onHide}
        onClick={(e) => { e.preventDefault(); open ? onHide() : onShow(); }}
        tabIndex={0}
        role="button"
        aria-describedby={`anchor-tooltip-${sourceId}`}
        className="cursor-pointer transition-colors duration-150 underline decoration-dotted underline-offset-4"
        style={{
          color: roleColor,
          textDecorationColor: open ? roleColor : `${roleColor}88`,
          background: open ? `${roleColor}18` : 'transparent',
        }}
      >
        {children}
      </span>
      {open && <AnchorTooltip source={source} anchor={anchor} anchorRect={rect} sourceId={sourceId} />}
    </>
  );
}

/**
 * Render bold markdown (`**text**`) inside plain text spans — same minimal
 * grammar as InlineCitations, kept for HOST/CHARACTER tags.
 */
function renderBold(text, keyPrefix) {
  if (!text) return null;
  const parts = [];
  const re = /\*\*([^*]+?)\*\*/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ k: 'plain', v: text.slice(last, m.index) });
    parts.push({ k: 'bold', v: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ k: 'plain', v: text.slice(last) });
  return parts.map((p, j) =>
    p.k === 'bold'
      ? <strong key={`${keyPrefix}-b-${j}-${i++}`} className="text-[#D4A84A]">{p.v}</strong>
      : <span key={`${keyPrefix}-t-${j}-${i++}`}>{p.v}</span>
  );
}

/**
 * Split a narration chunk's text on its anchors. Returns an array of
 * {kind: 'text'|'anchor', value, anchor?, source?, sourceId?} segments
 * preserving original ordering, with no overlap.
 */
function splitOnAnchors(text, anchors, registry) {
  if (!text || !anchors || anchors.length === 0) {
    return [{ kind: 'text', value: text || '' }];
  }
  // Compute non-overlapping match ranges for each anchor in order of
  // first appearance. If two anchors would overlap we keep the first
  // declared one and drop the second from this chunk.
  const matches = [];
  const taken = []; // [start, end) ranges already claimed
  for (const a of anchors) {
    if (!a?.text) continue;
    const idx = text.indexOf(a.text);
    if (idx < 0) continue;
    const range = [idx, idx + a.text.length];
    const overlaps = taken.some(([s, e]) => !(range[1] <= s || range[0] >= e));
    if (overlaps) continue;
    matches.push({ start: range[0], end: range[1], anchor: a });
    taken.push(range);
  }
  matches.sort((a, b) => a.start - b.start);

  const segs = [];
  let cursor = 0;
  for (const m of matches) {
    if (m.start > cursor) {
      segs.push({ kind: 'text', value: text.slice(cursor, m.start) });
    }
    const src = (registry || {})[m.anchor.source_id];
    segs.push({
      kind: 'anchor',
      value: text.slice(m.start, m.end),
      anchor: m.anchor,
      source: src,
      sourceId: m.anchor.source_id,
    });
    cursor = m.end;
  }
  if (cursor < text.length) {
    segs.push({ kind: 'text', value: text.slice(cursor) });
  }
  return segs;
}

export default function InlineAnchors({
  body = [],
  sourceRegistry = {},
  testIdPrefix = 'inline-anchors',
  className = 'font-mono text-base md:text-lg text-white/85 leading-relaxed space-y-4',
}) {
  if (!Array.isArray(body) || body.length === 0) return null;
  return (
    <div className={className} data-testid={`${testIdPrefix}-body`}>
      {body.map((chunk, ci) => {
        const text = chunk?.text || '';
        const anchors = Array.isArray(chunk?.anchors) ? chunk.anchors : [];
        const segs = splitOnAnchors(text, anchors, sourceRegistry);
        return (
          <p key={`chunk-${ci}`} data-testid={`${testIdPrefix}-chunk-${ci}`}>
            {segs.map((s, si) => {
              if (s.kind === 'text') {
                return <span key={`s-${ci}-${si}`}>{renderBold(s.value, `s-${ci}-${si}`)}</span>;
              }
              return (
                <AnchorSpan
                  key={`a-${ci}-${si}`}
                  anchor={s.anchor}
                  source={s.source}
                  sourceId={`${ci}-${si}-${s.sourceId}`}
                >
                  {s.value}
                </AnchorSpan>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}
